// Model Availability Alert Service
// Fetches model availability alerts from governance-service

import { api } from '@/shared/api/client'
import type { ModelResource } from '@/services/resource.service'
import { keysToCamel } from '@/shared/lib/utils'

/** Lifecycle values emitted by governance-service's model availability API. */
export type ModelAvailabilityLifecycleState =
  | 'unknown'
  | 'available'
  | 'deprecated'
  | 'missing'
  | 'replaced'
  | 'retired'
export type ModelAvailabilityReferenceAttribution = 'exact' | 'ambiguous'
export type ModelAvailabilityPresentation = 'none' | 'needs_migration' | 'blocked'

export interface ModelAvailabilityAlert {
  id: string
  organizationId: string
  providerId: string
  providerName: string
  providerType: string
  modelId: string
  modelFormat?: string
  modelVersion?: string
  lifecycleState?: ModelAvailabilityLifecycleState
  retirementDate?: string
  successorModelId?: string
  referenceCount: number
  ambiguousReferenceCount: number
  alertType: 'missing' | 'deprecated' | 'renamed' | 'replaced' | 'retired'
  severity: 'info' | 'warning' | 'critical'
  suggestedModel?: string
  suggestionSource?: string
  matchConfidence?: number
  message: string
  isResolved: boolean
  resolvedAt?: string
  resolvedBy?: string
  acknowledgedAt?: string
  acknowledgedBy?: string
  absenceObservations: number
  checkedAt: string
  createdAt: string
  updatedAt: string
}

export interface ModelAvailabilityAlertsResponse {
  alerts: ModelAvailabilityAlert[]
  total: number
}

export interface ModelAvailabilityReference {
  referenceKind: string
  subjectId?: string
  subjectName?: string
  subjectPath?: string
  attribution: ModelAvailabilityReferenceAttribution
  firstSeenAt: string
  lastSeenAt: string
  clearedAt?: string
}

export interface ModelAvailabilityReferencesResponse {
  references: ModelAvailabilityReference[]
  total: number
  page: number
  perPage: number
}

/** An explicit provider-qualified successor; the UI never infers this choice. */
export interface ModelAvailabilitySuccessor {
  providerId: string
  modelId: string
  modelFormat?: string
  modelVersion?: string
}

export interface ModelAvailabilityReplacementReference {
  referenceKind: string
  subjectId?: string
  subjectName?: string
  subjectPath?: string
  attribution: string
}

export interface ModelAvailabilityReplacementBlocker {
  code: string
  message: string
  referenceKind?: string
  subjectId?: string
}

export interface ModelAvailabilityReplacementEvidence {
  providerActive: boolean
  lifecycleAvailable: boolean
  registryAvailable: boolean
  regionCompatible: boolean
  modalityCompatible: boolean
  capabilitiesPreserved: boolean
  contextPreserved: boolean
  requiredCapabilities?: string[]
}

/** Server-authoritative capability for accepting durable replacements. */
export interface ModelAvailabilityReplacementExecutorStatus {
  ready: boolean
  message?: string
}

export interface ModelAvailabilityReplacementPreview {
  alertId: string
  alertRevision: number
  referenceSetHash: string
  successor: ModelAvailabilitySuccessor
  compatible: boolean
  references: ModelAvailabilityReplacementReference[]
  blockers: ModelAvailabilityReplacementBlocker[]
  evidence: ModelAvailabilityReplacementEvidence
  executor: ModelAvailabilityReplacementExecutorStatus
}

export interface ModelAvailabilityReplacementConfirmation {
  successor: ModelAvailabilitySuccessor
  alertRevision: number
  referenceSetHash: string
  confirmationAttemptId: string
}

/** Durable receipt returned after the server accepts a replacement request. */
export interface ModelAvailabilityReplacementConfirmationResult {
  status: 'submitted'
  operationId: string
  alertId: string
  referenceSetHash: string
}

export type ModelAvailabilityReplacementOperationStatus =
  | 'accepted'
  | 'preparing_missions'
  | 'packages_active'
  | 'cutover_ready'
  | 'succeeded'
  | 'retry_wait'
  | 'abandoning'
  | 'stale'
  | 'blocked'
  | 'failed'

/** Tenant-scoped status for an accepted safe-replacement operation. */
export interface ModelAvailabilityReplacementOperation {
  operationId: string
  status: ModelAvailabilityReplacementOperationStatus
  alertId: string
  referenceSetHash: string
  payloadHash: string
  confirmationAttemptId: string
  retryGeneration: number
  retryExhausted: boolean
  attemptCount: number
  lastErrorCode?: string
  lastErrorMessage?: string
  createdAt: string
  updatedAt: string
}

/**
 * Alert matching is intentionally provider-qualified. Model IDs are only
 * unique within a provider, so a same-named model elsewhere must not inherit
 * this provider's availability state.
 */
export function matchesModelAvailabilityAlert(
  alert: Pick<ModelAvailabilityAlert, 'providerId' | 'modelId' | 'modelFormat' | 'modelVersion'>,
  model: Pick<ModelResource, 'uri' | 'modelFormat' | 'modelVersion'>,
): boolean {
  const prefix = `${alert.providerId}:`
  if (!model.uri.startsWith(prefix)) return false

  if (model.uri.slice(prefix.length).toLowerCase() !== alert.modelId.toLowerCase()) return false

  return coordinatesMatch(alert.modelFormat, model.modelFormat)
    && coordinatesMatch(alert.modelVersion, model.modelVersion)
}

function coordinatesMatch(alertCoordinate?: string, modelCoordinate?: string): boolean {
  // Older providers can only report provider/model. Preserve that useful
  // fallback, but never let exact data match a different exact variant.
  if (!alertCoordinate || !modelCoordinate) return true
  return alertCoordinate.trim().toLowerCase() === modelCoordinate.trim().toLowerCase()
}

export function isPersistentModelAvailabilityAlert(
  alert: Pick<ModelAvailabilityAlert, 'severity' | 'lifecycleState'>,
): boolean {
  return alert.severity === 'critical' || alert.lifecycleState === 'retired'
}

/**
 * Keeps scanner lifecycle vocabulary separate from ModelResource's catalog
 * lifecycle vocabulary. The latter cannot represent missing or replaced.
 */
export function modelAvailabilityPresentation(
  alert: Pick<ModelAvailabilityAlert, 'lifecycleState'>,
): ModelAvailabilityPresentation {
  switch (alert.lifecycleState) {
    case 'deprecated':
      return 'needs_migration'
    case 'missing':
    case 'replaced':
    case 'retired':
      return 'blocked'
    case 'available':
    case 'unknown':
    default:
      return 'none'
  }
}

export function strongestModelAvailabilityPresentation(
  alerts: ReadonlyArray<Pick<ModelAvailabilityAlert, 'lifecycleState'>>,
): ModelAvailabilityPresentation {
  let presentation: ModelAvailabilityPresentation = 'none'
  for (const alert of alerts) {
    const candidate = modelAvailabilityPresentation(alert)
    if (candidate === 'blocked') return candidate
    if (candidate === 'needs_migration') presentation = candidate
  }
  return presentation
}

function countOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0
}

/** Normalize count fields omitted by older governance-service responses. */
export function normalizeModelAvailabilityAlertsResponse(
  response: ModelAvailabilityAlertsResponse,
): ModelAvailabilityAlertsResponse {
  const alerts = Array.isArray(response.alerts)
    ? response.alerts.map(alert => ({
      ...alert,
      referenceCount: countOrZero(alert.referenceCount),
      ambiguousReferenceCount: countOrZero(alert.ambiguousReferenceCount),
    }))
    : []

  return {
    ...response,
    alerts,
    total: countOrZero(response.total) || alerts.length,
  }
}

function normalizeModelAvailabilityReferencesResponse(
  response: ModelAvailabilityReferencesResponse,
  fallbackPerPage: number,
): ModelAvailabilityReferencesResponse {
  const legacyResponse = response as ModelAvailabilityReferencesResponse & { pageSize?: number }
  return {
    ...response,
    references: Array.isArray(response.references) ? response.references : [],
    total: countOrZero(response.total),
    page: countOrZero(response.page) || 1,
    perPage: countOrZero(response.perPage) || countOrZero(legacyResponse.pageSize) || fallbackPerPage,
  }
}

class ModelAvailabilityService {
  async getAlerts(resolved = false): Promise<ModelAvailabilityAlertsResponse> {
    const response = await api.get<ModelAvailabilityAlertsResponse>(
      'governance/model-availability/alerts',
      { resolved: String(resolved) }
    )
    return normalizeModelAvailabilityAlertsResponse(response)
  }

  async acknowledgeAlert(alertId: string): Promise<{ status: string }> {
    return api.patch<{ status: string }>(`governance/model-availability/alerts/${alertId}/acknowledge`)
  }

  async getAlertReferences(
    alertId: string,
    page = 1,
    pageSize = 10,
  ): Promise<ModelAvailabilityReferencesResponse> {
    const response = await api.get<ModelAvailabilityReferencesResponse>(
      `governance/model-availability/alerts/${encodeURIComponent(alertId)}/references`,
      { page, per_page: pageSize },
    )
    return normalizeModelAvailabilityReferencesResponse(response, pageSize)
  }

  async previewReplacement(
    alertId: string,
    successor: ModelAvailabilitySuccessor,
  ): Promise<ModelAvailabilityReplacementPreview> {
    try {
      return await api.post<ModelAvailabilityReplacementPreview>(
        `governance/model-availability/alerts/${encodeURIComponent(alertId)}/replacement-preview`,
        { successor },
      )
    } catch (error) {
      // The backend intentionally returns a complete preview with HTTP 422 so
      // operators can see every blocker. ApiClient only case-converts success
      // bodies, therefore adapt this error body at this boundary.
      const apiError = error as { status?: unknown; data?: unknown }
      if (apiError.status === 422 && apiError.data && typeof apiError.data === 'object') {
        return keysToCamel<ModelAvailabilityReplacementPreview>(apiError.data)
      }
      throw error
    }
  }

  async confirmReplacement(
    alertId: string,
    confirmation: ModelAvailabilityReplacementConfirmation,
  ): Promise<ModelAvailabilityReplacementConfirmationResult> {
    return api.post<ModelAvailabilityReplacementConfirmationResult>(
      `governance/model-availability/alerts/${encodeURIComponent(alertId)}/replacement`,
      confirmation,
    )
  }

  /**
   * Reads one operation within the authenticated tenant. The service never
   * exposes an organization parameter: the API derives it from the session.
   */
  async getReplacementOperation(operationId: string): Promise<ModelAvailabilityReplacementOperation> {
    return api.get<ModelAvailabilityReplacementOperation>(
      `governance/model-availability/replacement-operations/${encodeURIComponent(operationId)}`,
    )
  }
}

export const modelAvailabilityService = new ModelAvailabilityService()
